// libraries.
import { useState, useEffect } from "react";
import { Spinner } from "react-bootstrap";

// shared.
import { Tab } from "@shared/Tabs";
import { useUserProfile } from "@shared/GoogleLoginButton/user-slice/useUserProfile.hook";

// styles.
import { electricPurple600, monserratSemiBold, scarpaGray500 } from "@habitech/shared";
import { StyledTabsContainer } from "../styles";

// store.
import { useGetRules } from "@store/apiSlices/incentiveRules/rules.hook";
import { useGetSubRutasQuery } from "@store/apiSlices/incentiveRules/subRutas.endpoints";

// components.
import { SubRutaDashboard } from "./SubRutaDashboard";
import { SinClasificarPanel } from "./SinClasificarPanel";

const SCHEMA_BY_COUNTRY: Record<string, "inventory_co" | "inventory_mx"> = {
  CO: "inventory_co",
  MX: "inventory_mx",
};

export const RulesTabs = () => {
  const { country } = useUserProfile();
  const resolvedCountry = country || "CO";
  const schema = SCHEMA_BY_COUNTRY[resolvedCountry] ?? "inventory_co";

  const { isLoading: rulesLoading, isFetching: rulesFetching } = useGetRules(resolvedCountry);
  const {
    data: subRutas = [],
    isLoading: subRutasLoading,
    isFetching: subRutasFetching,
  } = useGetSubRutasQuery({ schema });

  const loading = rulesLoading || rulesFetching || subRutasLoading || subRutasFetching;

  // undefined = aún no inicializado (esperando datos de la API)
  // null      = usuario eligió la tab "Sin clasificar"
  // number    = usuario eligió una sub-ruta concreta
  const [activeSubRutaId, setActiveSubRutaId] = useState<number | null | undefined>(undefined);

  useEffect(() => {
    if (subRutas.length > 0 && activeSubRutaId === undefined) {
      setActiveSubRutaId(subRutas[0].id);
    }
  }, [subRutas, activeSubRutaId]);

  return (
    <StyledTabsContainer className="d-flex flex-column flex-grow-1 overflow-hidden">
      <div className="d-flex tabs-header flex-shrink-0">
        {subRutas.map(({ id, name }) => (
          <Tab
            key={id}
            active={activeSubRutaId === id}
            onClick={() => setActiveSubRutaId(id)}
            title={
              <span style={{ fontFamily: monserratSemiBold, fontWeight: 600 }}>
                {name}
              </span>
            }
            icon={
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor:
                    activeSubRutaId === id ? electricPurple600 : scarpaGray500,
                  display: "inline-block",
                }}
              />
            }
          />
        ))}

        {/* Tab estática para reglas sin clasificar */}
        <Tab
          active={activeSubRutaId === null}
          onClick={() => setActiveSubRutaId(null)}
          title={
            <span style={{ fontFamily: monserratSemiBold, fontWeight: 600 }}>
              📋 Sin clasificar
            </span>
          }
          icon={
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: activeSubRutaId === null ? electricPurple600 : scarpaGray500,
                display: "inline-block",
              }}
            />
          }
        />
      </div>

      <div className="tabs-content">
        {loading && (
          <div className="tabs-loader">
            <Spinner animation="border" variant="secondary" />
          </div>
        )}
        {!loading && activeSubRutaId === null && <SinClasificarPanel />}
        {!loading && activeSubRutaId !== null && (
          <SubRutaDashboard
            subRutaId={activeSubRutaId}
            subRutaName={subRutas.find((s) => s.id === activeSubRutaId)?.name ?? ""}
          />
        )}
      </div>
    </StyledTabsContainer>
  );
};
